import React, { useEffect, useMemo, useRef, useState } from 'react';
import ShopService from '../service/ShopService';
import { useHistory, useLocation } from "react-router-dom";
import {
    HiOutlinePlus, HiOutlinePencil, HiOutlinePhotograph, HiOutlineHome, HiOutlineEmojiSad, HiOutlineCollection,
    HiOutlineSearch, HiOutlineViewGrid, HiOutlineShoppingBag, HiOutlineExclamation, HiOutlineTrash, HiOutlineTag, HiX
} from 'react-icons/hi';
import { MdNavigateNext } from 'react-icons/md';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import SidePanel from '../components/SidePanel';
import ImageCropper from '../components/ImageCropper';
import { CATEGORY_TILE, CATEGORY_WIDE, checkPhotoFile } from '../util/imageFile';

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'home', label: 'Home page' },
    { key: 'empty', label: 'No products' },
    { key: 'noimage', label: 'No image' }
]

const subsOf = (c) => c.subCategories || []
const countOf = (s) => Number(s.itemsCount) || 0
const productsIn = (c) => subsOf(c).reduce((sum, s) => sum + countOf(s), 0)
const formatCount = (n) => new Intl.NumberFormat('en-IN').format(n)
const plural = (n, one, many = one + 's') => `${formatCount(n)} ${n === 1 ? one : many}`
const naturalSort = (list) => [...(list || [])].sort((a, b) => String(a.size).localeCompare(String(b.size), undefined, { numeric: true }))

// filter rules for main categories and sub-categories
const MATCHES = {
    all: () => true,
    home: (c) => c.inMainDisplay === true,
    empty: (c, isMain) => (isMain ? productsIn(c) === 0 : countOf(c) === 0),
    noimage: (c) => !c.image
}

const initials = (name) => ((name || '').match(/[\p{L}\p{N}]+/gu) || ['?']).slice(0, 2).map(w => w[0]).join('').toUpperCase()

// Highlights the part of `text` that matches the search
const Highlight = ({ text, query }) => {
    const at = query ? text.toLowerCase().indexOf(query) : -1
    if (at < 0) return text
    return <>{text.slice(0, at)}<mark className='mv-inv-mark'>{text.slice(at, at + query.length)}</mark>{text.slice(at + query.length)}</>
}

// The whole image always shows (contain), over a blurred copy of itself so the box never looks empty
const FitImage = ({ src, alt = '' }) => (
    <span className='mv-fit'>
        <img className='mv-fit-bg' src={src} alt='' aria-hidden='true' loading='lazy' />
        <img className='mv-fit-img' src={src} alt={alt} loading='lazy' />
    </span>
)

const SubRow = ({ sub, query, onOpen, onEdit }) => {
    const count = countOf(sub)
    return (
        <li className='mv-inv-sub'>
            <button type='button' className='mv-inv-sub-link' onClick={() => onOpen(sub.categoryId)}>
                <span className='mv-inv-thumb' aria-hidden='true'>
                    {sub.image ? <img src={sub.image} alt='' loading='lazy' /> : initials(sub.categoryName)}
                </span>
                <span className='mv-inv-sub-name'>
                    <Highlight text={sub.categoryName} query={query} />
                    {sub.inMainDisplay && <HiOutlineHome className='mv-inv-home' title='Shown on the home page' />}
                </span>
                <span className={count === 0 ? 'mv-inv-count is-empty' : 'mv-inv-count'}
                    title={plural(count, 'product')}>{formatCount(count)}</span>
                <MdNavigateNext className='mv-inv-chevron' aria-hidden='true' />
            </button>
            <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label={`Edit ${sub.categoryName}`} onClick={() => onEdit(sub)}>
                <HiOutlinePencil />
            </button>
        </li>
    )
}

const CategoryCard = ({ cat, subs, hiddenSubs, query, flash, onOpen, onEdit, onAddSub }) => {
    const total = subsOf(cat).length
    const products = productsIn(cat)
    return (
        <article className={flash ? 'mv-inv-card is-flash' : 'mv-inv-card'}>
            <div className={cat.image ? 'mv-inv-media' : 'mv-inv-media is-empty'}>
                {cat.image ? <FitImage src={cat.image} alt={cat.categoryName} /> :
                    <button type='button' className='mv-inv-noimage' onClick={() => onEdit(cat)}>
                        <HiOutlinePhotograph aria-hidden='true' /> Add image
                    </button>}
                {cat.inMainDisplay && <span className='mv-chip mv-chip-accent mv-inv-badge'><HiOutlineHome aria-hidden='true' /> Home page</span>}
            </div>

            <div className='mv-inv-card-head'>
                <div className='mv-inv-card-titles'>
                    <h3 className='mv-inv-card-name'><Highlight text={cat.categoryName} query={query} /></h3>
                    <p className='mv-inv-card-meta'>
                        {plural(total, 'sub-category', 'sub-categories')} · {plural(products, 'product')}
                    </p>
                </div>
                <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label={`Edit ${cat.categoryName}`} onClick={() => onEdit(cat)}>
                    <HiOutlinePencil />
                </button>
            </div>

            {subs.length > 0 &&
                <ul className='mv-inv-subs'>
                    {subs.map(s => <SubRow key={s.categoryId} sub={s} query={query} onOpen={onOpen} onEdit={onEdit} />)}
                </ul>}

            {hiddenSubs > 0 && <p className='mv-inv-hidden'>{plural(hiddenSubs, 'more sub-category', 'more sub-categories')} hidden by the filter</p>}

            {hiddenSubs === 0 &&
                <button type='button' className='mv-inv-add-sub' onClick={() => onAddSub(cat)}>
                    <HiOutlinePlus aria-hidden='true' /> {total === 0 ? 'Add the first sub-category' : 'Add sub-category'}
                </button>}
        </article>
    )
}

const BoardSkeleton = () => (
    <div className='mv-inv-board' role='status' aria-label='Loading categories'>
        {[3, 1, 4, 2, 0, 3].map((rows, i) =>
            <div key={i} className='mv-inv-card' aria-hidden='true'>
                <Skeleton className='mv-inv-media' height='auto' radius='0' />
                <div className='mv-inv-card-head'>
                    <div className='mv-inv-card-titles'>
                        <Skeleton width='55%' height={20} />
                        <Skeleton width='40%' height={12} style={{ marginTop: 8 }} />
                    </div>
                </div>
                <div className='mv-inv-subs'>
                    {Array.from({ length: rows }, (_, r) => <Skeleton key={r} height={32} style={{ margin: '6px 0' }} />)}
                </div>
            </div>
        )}
    </div>
)

const BrandRow = ({ brand, query, flash, onEdit }) => {
    const count = Number(brand.productCount) || 0
    return (
        <li className={flash ? 'mv-inv-brand is-flash' : 'mv-inv-brand'}>
            <button type='button' className='mv-inv-brand-link' onClick={() => onEdit(brand)} aria-label={`Edit ${brand.name}`}>
                <span className='mv-inv-thumb' aria-hidden='true'>{initials(brand.name)}</span>
                <span className='mv-inv-sub-name'><Highlight text={brand.name} query={query} /></span>
                <span className={count === 0 ? 'mv-inv-count is-empty' : 'mv-inv-count'}>{plural(count, 'product')}</span>
                <HiOutlinePencil className='mv-inv-brand-edit' aria-hidden='true' />
            </button>
        </li>
    )
}

const TABS = ['categories', 'sizes', 'brands']

const ShopListCard = (props) => {

    const { list, sizeData, categoriesError, sizesError, brands, brandsError } = props;
    const [isLoading, setIsLoading] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [file, setFile] = useState(null)
    const [selectedWide, setSelectedWide] = useState(null)
    const [widePreview, setWidePreview] = useState(null)
    const [cropSource, setCropSource] = useState(null)
    const [original, setOriginal] = useState(null)
    const categoryFileRef = useRef(null)
    const [category, setCategory] = useState('')
    const [parentId, setParentId] = useState(0)
    const [categoryId, setCategoryId] = useState(0)
    const [showCategoryAdd, setShowCategoryAdd] = useState(false)
    const location = useLocation()
    // ?tab=sizes / ?tab=brands opens that tab (the product editor links to Brands)
    const [tab, setTab] = useState(() => {
        const asked = new URLSearchParams(location.search).get('tab')
        return TABS.includes(asked) ? asked : 'categories'
    })
    const isCategoryPage = tab === 'categories'
    const [brandSearch, setBrandSearch] = useState('')
    const [brandEdit, setBrandEdit] = useState(null)
    const [brandName, setBrandName] = useState('')
    const [showSizeAdd, setShowSizeAdd] = useState(false)
    const [sizeId, setSizeId] = useState(0)
    const [size, setSize] = useState('')
    const [sizePattern, setSizePattern] = useState('')
    const [inMainDisplay, setInMainDisplay] = useState(false)
    const [formError, setFormError] = useState('')
    const [confirmDelete, setConfirmDelete] = useState(null)
    const [toast, setToast] = useState('')
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState('all')
    const [flashId, setFlashId] = useState(null)

    let history = useHistory();

    useEffect(() => {
        if (!flashId) return undefined
        const timer = setTimeout(() => setFlashId(null), 1400)
        return () => clearTimeout(timer)
    }, [flashId])

    const query = search.trim().toLowerCase()

    // which categories (and which of their sub-categories) the search and filter leave on the board
    const board = useMemo(() => {
        if (!list) return []
        const matchQ = (c) => !query || (c.categoryName || '').toLowerCase().includes(query)
        const matchF = MATCHES[filter]
        return list.map(cat => {
            const all = subsOf(cat)
            const mainHit = matchQ(cat) && matchF(cat, true)
            const subs = all.filter(s => (matchQ(cat) || matchQ(s)) && matchF(s, false))
            const show = mainHit || subs.length > 0
            // a main category that matches by name shows all its sub-categories when no filter is set
            const shown = mainHit && filter === 'all' ? all : subs
            return show ? { cat, subs: shown, hiddenSubs: all.length - shown.length } : null
        }).filter(Boolean)
    }, [list, query, filter])

    const stats = useMemo(() => {
        const mains = list || []
        const subs = mains.flatMap(subsOf)
        const everything = [...mains, ...subs]
        return {
            mains: mains.length,
            subs: subs.length,
            products: subs.reduce((sum, s) => sum + countOf(s), 0),
            home: everything.filter(MATCHES.home).length,
            empty: subs.filter(s => countOf(s) === 0).length + mains.filter(c => subsOf(c).length === 0).length,
            noimage: everything.filter(MATCHES.noimage).length
        }
    }, [list])

    const brandQuery = brandSearch.trim().toLowerCase()
    const shownBrands = useMemo(() => (brands || []).filter(b => !brandQuery || b.name.toLowerCase().includes(brandQuery)),
        [brands, brandQuery])

    const sizeCount = sizeData ? (sizeData.months || []).length + (sizeData.years || []).length : null

    const resetCategoryForm = () => {
        setCategory('')
        setCategoryId(0)
        setParentId(0)
        setInMainDisplay(false)
        setFile(null)
        setSelectedFile(null)
        setSelectedWide(null)
        setWidePreview(null)
        setOriginal(null)
        setFormError('')
    }

    const resetSizeForm = () => {
        setSize('')
        setSizeId(0)
        setSizePattern('')
        setFormError('')
    }

    const openNewCategory = (parent) => {
        resetCategoryForm()
        if (parent) setParentId(parent.categoryId)
        setShowCategoryAdd(true)
    }

    const closeCategoryForm = () => setShowCategoryAdd(false)

    const openNewSize = (pattern) => {
        resetSizeForm()
        if (pattern) setSizePattern(pattern)
        setShowSizeAdd(true)
    }

    const closeSizeForm = () => setShowSizeAdd(false)

    const callCategoryList = (param) => {
        history.push({
            pathname: '/shop/items/list/' + param,
            state: { category: "test" }
        })
    }

    const updateFormCategory = (cat) => {
        setFormError('')
        setSelectedFile(null)
        setSelectedWide(null)
        setOriginal(null)
        setFile(cat.image || null)
        setWidePreview(cat.imageWide || null)
        setCategory(cat.categoryName)
        setCategoryId(cat.categoryId)
        setParentId(cat.parentId || 0)
        setInMainDisplay(cat.inMainDisplay === true)
        setShowCategoryAdd(true)
    }

    const updateFormSize = (sizeVal, sizeIdVal, sizePatternVal) => {
        setFormError('')
        setSize(sizeVal)
        setSizeId(sizeIdVal)
        setSizePattern(sizePatternVal || '')
        setShowSizeAdd(true)
    }

    // picking an image opens the cropper: a 3:4 tile, then a 16:9 wide version of the same photo
    const onFileChange = event => {
        const picked = event.target.files[0]
        event.target.value = null
        if (!picked) return
        const problem = checkPhotoFile(picked)
        if (problem) {
            setFormError(problem)
        } else {
            setFormError('')
            setCropSource(picked)
        }
    };

    const onCropped = (results) => {
        setOriginal(cropSource)
        setCropSource(null)
        setSelectedFile(results.tile.file)
        setFile(results.tile.url)
        setSelectedWide(results.wide.file)
        setWidePreview(results.wide.url)
    }

    const onCropCancel = (message) => {
        setCropSource(null)
        if (message) setFormError(message)
    }

    const pickCategoryImage = () => categoryFileRef.current && categoryFileRef.current.click()

    const onAddCategory = event => {
        event.preventDefault();
        if (category == null || category.trim().length < 2) {
            setFormError("Please enter a category name (at least 2 characters).")
            return
        }
        setIsLoading(true);
        setFormError('')
        const formData = new FormData();
        formData.append("parentId", parentId);
        formData.append("categoryName", category);
        formData.append("categoryId", categoryId);
        formData.append("inMainDisplay", inMainDisplay)
        if (selectedFile != null) {
            formData.append("file", selectedFile);
            if (selectedWide != null) formData.append("wideFile", selectedWide);
        }

        ShopService.updateCategory(formData).then(res => {
            setIsLoading(false);
            if (res.status === 200) {
                props.reloadAction()
                setToast(categoryId > 0 ? 'Category updated' : 'Category added')
                // highlight the card that changed: the edited category, or the parent of a sub-category
                setFlashId(Number(parentId) > 0 ? Number(parentId) : (categoryId || null))
                closeCategoryForm()
            }
        }).catch(error => {
            setIsLoading(false);
            setFormError("Could not save the category. Please try again.")
        });
    };

    const onAddSize = event => {
        event.preventDefault();
        if (size == null || size.trim().length < 2) {
            setFormError("Please enter a size (at least 2 characters).")
            return
        }
        if (!sizePattern) {
            setFormError("Please choose whether the size is in months or years.")
            return
        }
        setIsLoading(true);
        setFormError('')
        const formData = new FormData();
        if (sizeId != null && sizeId > 0) {
            formData.append("sizeId", sizeId);
        }
        formData.append("size", size);
        formData.append("sizePattern", sizePattern);

        ShopService.updateSize(formData).then(res => {
            setIsLoading(false);
            if (res.status === 200) {
                props.reloadActionSizes()
                setToast(sizeId > 0 ? 'Size updated' : 'Size added')
                closeSizeForm()
            }
        }).catch(error => {
            setIsLoading(false);
            setFormError("Could not save the size. Please try again.")
        });
    };

    const openBrand = (brand) => {
        setFormError('')
        setBrandName(brand ? brand.name : '')
        setBrandEdit(brand || { id: 0, name: '', productCount: 0 })
    }

    const closeBrandForm = () => setBrandEdit(null)

    const onSaveBrand = event => {
        event.preventDefault();
        const name = brandName.trim()
        if (name.length < 1) {
            setFormError("Please enter a brand name.")
            return
        }
        setIsLoading(true);
        setFormError('')
        ShopService.saveBrand(brandEdit.id, name).then(res => {
            setIsLoading(false);
            props.reloadActionBrands()
            setToast(brandEdit.id > 0 ? 'Brand updated' : 'Brand added')
            setFlashId(res.data && res.data.data ? 'brand-' + res.data.data.id : null)
            closeBrandForm()
        }).catch(error => {
            setIsLoading(false);
            const msg = error.response && error.response.data && error.response.data.msg
            setFormError(msg || "Could not save the brand. Please try again.")
        });
    };

    const onDeleteConfirmed = () => {
        const target = confirmDelete
        setIsLoading(true);
        const request = target.type === 'category' ? ShopService.deleteCategory(target.id)
            : target.type === 'brand' ? ShopService.deleteBrand(target.id)
                : ShopService.deleteSize(target.id)

        request.then(res => {
            setIsLoading(false);
            setConfirmDelete(null)
            if (res.status === 200) {
                if (target.type === 'category') {
                    props.reloadAction()
                    closeCategoryForm()
                } else if (target.type === 'brand') {
                    props.reloadActionBrands()
                    closeBrandForm()
                } else {
                    props.reloadActionSizes()
                    closeSizeForm()
                }
                setToast(`${target.name} deleted`)
            }
        }).catch(error => {
            setIsLoading(false);
            setConfirmDelete(null)
            const msg = error.response && error.response.data && error.response.data.msg
            setFormError(msg || `Could not delete ${target.name}. It may still be in use.`)
        });
    };

    // the tab is kept in the URL, so Back from a product or a shared link opens the same tab
    const switchTab = (next) => {
        setTab(next)
        setFormError('')
        history.replace({ pathname: location.pathname, search: next === 'categories' ? '' : '?tab=' + next })
    }

    const showFilter = (key) => {
        switchTab('categories')
        setFilter(key)
    }

    const parentName = (list || []).find(c => String(c.categoryId) === String(parentId))
    const categoryTitle = categoryId > 0
        ? (Number(parentId) > 0 ? 'Edit sub-category' : 'Edit category')
        : (Number(parentId) > 0 ? `New sub-category${parentName ? ' in ' + parentName.categoryName : ''}` : 'New category')

    // also while the cropper is open, so its Escape doesn't close the panel behind it
    const panelBusy = isLoading || confirmDelete != null || cropSource != null
    const closeFor = { 'inv-category-form': closeCategoryForm, 'inv-size-form': closeSizeForm, 'inv-brand-form': closeBrandForm }

    const formActions = (formId, isEdit, onDelete, addLabel) => (
        <>
            {isEdit &&
                <button type='button' className='mv-icon-btn mv-inv-delete' aria-label='Delete' title='Delete' disabled={isLoading} onClick={onDelete}>
                    <HiOutlineTrash />
                </button>}
            <span className='mv-panel-spacer' />
            <button type='button' className='mv-btn mv-btn-secondary' onClick={closeFor[formId]}
                disabled={isLoading}>Cancel</button>
            <button type='submit' form={formId} className='mv-btn mv-btn-primary' disabled={isLoading}>
                {isLoading ? 'Saving…' : (isEdit ? 'Save changes' : addLabel)}
            </button>
        </>
    )

    const renderCategoryForm = () => (
        <form id='inv-category-form' className='mv-form' onSubmit={onAddCategory} noValidate>
            {file ?
                <div className='mv-inv-photos'>
                    <figure className='mv-inv-photo is-tile'>
                        <img src={file} alt='Category tile (3:4)' />
                        <figcaption>Tile · 3:4</figcaption>
                    </figure>
                    <figure className='mv-inv-photo is-wide'>
                        {widePreview ? <img src={widePreview} alt='Wide version (16:9)' /> :
                            <img src={file} alt='' className='is-from-tile' />}
                        <figcaption>{widePreview ? 'Wide · 16:9' : 'Wide · cut from the tile'}</figcaption>
                    </figure>
                </div> :
                <button type='button' className='mv-inv-upload' onClick={pickCategoryImage}>
                    <span className='mv-upload-empty'><HiOutlinePhotograph /> Add image</span>
                </button>}
            <input id='category-image' ref={categoryFileRef} type='file' accept='image/jpeg,image/png,image/webp'
                onChange={onFileChange} hidden />
            <div className='mv-inv-photo-actions'>
                {file && <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={pickCategoryImage}>Change image</button>}
                {original && <button type='button' className='mv-link-btn mv-recrop' onClick={() => setCropSource(original)}>Crop again</button>}
            </div>
            <p className='mv-field-hint'>You'll crop one photo twice: a 3:4 tile for the home page and menus, and a 16:9 wide version.</p>
            <div className='mv-field'>
                <label className='mv-label' htmlFor='category-name'>Name</label>
                <input id='category-name' className='mv-input' placeholder='e.g. Baby Girls' value={category || ''}
                    onChange={e => setCategory(e.target.value)} />
            </div>
            <div className='mv-field'>
                <label className='mv-label' htmlFor='category-parent'>Parent category</label>
                <select id='category-parent' className='mv-select' value={parentId} onChange={e => setParentId(e.target.value)}>
                    <option value="0">None (top-level category)</option>
                    {list != null && list.filter(e => e.categoryId !== categoryId).map(e =>
                        <option key={e.categoryId} value={e.categoryId}>{e.categoryName}</option>)}
                </select>
            </div>
            <label className='mv-check'>
                <input type='checkbox' checked={inMainDisplay} onChange={e => setInMainDisplay(e.target.checked)} />
                Show on the home page
            </label>
            {formError && <div className='mv-alert mv-alert-error' role='alert'>{formError}</div>}
        </form>
    )

    const renderSizeForm = () => (
        <form id='inv-size-form' className='mv-form' onSubmit={onAddSize} noValidate>
            <div className='mv-field'>
                <label className='mv-label' htmlFor='size-name'>Size</label>
                <input id='size-name' className='mv-input' placeholder='e.g. 6-9m or 3-4Y' value={size || ''}
                    onChange={e => setSize(e.target.value)} />
            </div>
            <div className='mv-field'>
                <label className='mv-label' htmlFor='size-pattern'>Measured in</label>
                <select id='size-pattern' className='mv-select' value={sizePattern || ''} onChange={e => setSizePattern(e.target.value)}>
                    <option value="">Choose…</option>
                    <option value="MONTH">Months</option>
                    <option value="YEAR">Years</option>
                </select>
            </div>
            {formError && <div className='mv-alert mv-alert-error' role='alert'>{formError}</div>}
        </form>
    )

    const brandUses = brandEdit ? Number(brandEdit.productCount) || 0 : 0

    const renderBrandForm = () => (
        <form id='inv-brand-form' className='mv-form' onSubmit={onSaveBrand} noValidate>
            <div className='mv-field'>
                <label className='mv-label' htmlFor='brand-name'>Name</label>
                <input id='brand-name' className='mv-input' placeholder='e.g. Babyhug' maxLength={80} value={brandName}
                    onChange={e => setBrandName(e.target.value)} />
                <p className='mv-field-hint'>Shown on the product page, above the product name.</p>
            </div>
            {brandEdit && brandEdit.id > 0 && brandUses > 0 &&
                <p className='mv-field-hint'>
                    Used by {plural(brandUses, 'product')}. Change their brand to delete it.
                </p>}
            {formError && <div className='mv-alert mv-alert-error' role='alert'>{formError}</div>}
        </form>
    )

    const renderSizeGroup = (title, pattern, sizes) => (
        <section className='mv-card mv-inv-sizes'>
            <div className='mv-inv-sizes-head'>
                <h2 className='mv-card-title'>{title} <span className='mv-inv-tab-count'>{(sizes || []).length}</span></h2>
                <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label={`Add a size in ${title.toLowerCase()}`}
                    onClick={() => openNewSize(pattern)}>
                    <HiOutlinePlus />
                </button>
            </div>
            {sizes && sizes.length > 0 ?
                <div className='mv-size-chips'>
                    {naturalSort(sizes).map(e =>
                        <button type='button' key={e.id} className={showSizeAdd && e.id === sizeId ? 'mv-filter-chip active' : 'mv-filter-chip'}
                            onClick={() => updateFormSize(e.size, e.id, e.sizePattern)} aria-label={`Edit size ${e.size}`}>
                            {e.size}
                        </button>
                    )}
                </div> :
                <p className='mv-page-sub'>No sizes yet.</p>}
        </section>
    )

    const statTile = (icon, label, value, tone, onClick) => {
        const body = <>
            <span className='mv-inv-stat-icon' aria-hidden='true'>{icon}</span>
            <span className='mv-inv-stat-text'>
                <span className='mv-inv-stat-value'>{list == null ? <Skeleton width={48} height={22} /> : value}</span>
                <span className='mv-inv-stat-label'>{label}</span>
            </span>
        </>
        return onClick
            ? <button type='button' className={`mv-inv-stat ${tone}`} onClick={onClick}>{body}</button>
            : <div className={`mv-inv-stat ${tone}`}>{body}</div>
    }

    return (
        <div className='mv-inv'>
            <div className='mv-inv-stats'>
                {statTile(<HiOutlineViewGrid />, `Categories · ${formatCount(stats.subs)} sub`, formatCount(stats.mains), 'is-primary')}
                {statTile(<HiOutlineShoppingBag />, 'Products listed', formatCount(stats.products), 'is-success')}
                {statTile(<HiOutlineHome />, 'On the home page', formatCount(stats.home), 'is-accent', () => showFilter('home'))}
                {statTile(<HiOutlineExclamation />, 'With no products', formatCount(stats.empty),
                    stats.empty > 0 ? 'is-warning' : 'is-muted', () => showFilter('empty'))}
            </div>

            <div className='mv-inv-bar'>
                <div className='mv-tabs' role='tablist' aria-label='Inventory sections'>
                    <button type='button' role='tab' id='inv-tab-categories' aria-controls='inv-panel-categories' aria-selected={isCategoryPage}
                        className={isCategoryPage ? 'mv-tab active' : 'mv-tab'} onClick={() => switchTab('categories')}>
                        Categories <span className='mv-inv-tab-count'>{list ? stats.mains + stats.subs : '–'}</span>
                    </button>
                    <button type='button' role='tab' id='inv-tab-sizes' aria-controls='inv-panel-sizes' aria-selected={tab === 'sizes'}
                        className={tab === 'sizes' ? 'mv-tab active' : 'mv-tab'} onClick={() => switchTab('sizes')}>
                        Sizes <span className='mv-inv-tab-count'>{sizeCount ?? '–'}</span>
                    </button>
                    <button type='button' role='tab' id='inv-tab-brands' aria-controls='inv-panel-brands' aria-selected={tab === 'brands'}
                        className={tab === 'brands' ? 'mv-tab active' : 'mv-tab'} onClick={() => switchTab('brands')}>
                        Brands <span className='mv-inv-tab-count'>{brands ? brands.length : '–'}</span>
                    </button>
                </div>

                <div className={tab === 'sizes' ? 'mv-search mv-inv-search is-off' : 'mv-search mv-inv-search'}>
                    <HiOutlineSearch aria-hidden='true' />
                    {tab === 'brands'
                        ? <input type='search' className='mv-input' placeholder='Search brands' aria-label='Search brands'
                            value={brandSearch} onChange={e => setBrandSearch(e.target.value)} />
                        : <input type='search' className='mv-input' placeholder='Search categories' aria-label='Search categories'
                            value={search} onChange={e => setSearch(e.target.value)} />}
                </div>

                <button type='button' className='mv-btn mv-btn-primary mv-btn-sm mv-inv-new'
                    onClick={() => (tab === 'categories' ? openNewCategory() : tab === 'sizes' ? openNewSize() : openBrand(null))}>
                    <HiOutlinePlus /> {tab === 'categories' ? 'New category' : tab === 'sizes' ? 'New size' : 'New brand'}
                </button>
            </div>

            <div id='inv-panel-categories' role='tabpanel' aria-labelledby='inv-tab-categories' hidden={!isCategoryPage}>
                <div className='mv-chip-row mv-inv-filters' role='group' aria-label='Show'>
                    {FILTERS.map(f =>
                        <button type='button' key={f.key} className={filter === f.key ? 'mv-filter-chip active' : 'mv-filter-chip'}
                            aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                            {f.label}
                            {f.key !== 'all' && <span className='mv-inv-chip-count'>{list != null ? stats[f.key] : ''}</span>}
                        </button>
                    )}
                </div>

                {categoriesError && list == null &&
                    <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load categories'
                        text='Please check your connection and try again.' actionLabel='Try again' onAction={props.reloadAction} />}

                {!categoriesError && list == null && <BoardSkeleton />}

                {list != null && list.length === 0 &&
                    <EmptyState icon={<HiOutlineCollection />} title='No categories yet'
                        text='Create your first category to start organising products.' actionLabel='New category' onAction={() => openNewCategory()} />}

                {list != null && list.length > 0 && board.length === 0 &&
                    <EmptyState icon={<HiOutlineSearch />} title='Nothing matches'
                        text='Try another name or show all categories.' actionLabel='Clear search and filter'
                        onAction={() => { setSearch(''); setFilter('all') }} />}

                {board.length > 0 &&
                    <div className='mv-inv-board'>
                        {board.map(({ cat, subs, hiddenSubs }) =>
                            <CategoryCard key={cat.categoryId} cat={cat} subs={subs} hiddenSubs={hiddenSubs} query={query}
                                flash={flashId != null && String(flashId) === String(cat.categoryId)}
                                onOpen={callCategoryList} onEdit={updateFormCategory} onAddSub={openNewCategory} />
                        )}
                    </div>}

                {(query || filter !== 'all') && board.length > 0 &&
                    <button type='button' className='mv-inv-clear' onClick={() => { setSearch(''); setFilter('all') }}>
                        <HiX aria-hidden='true' /> Clear search and filter
                    </button>}
            </div>

            <div id='inv-panel-sizes' role='tabpanel' aria-labelledby='inv-tab-sizes' hidden={tab !== 'sizes'}>
                {sizesError && sizeData == null &&
                    <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load sizes'
                        text='Please check your connection and try again.' actionLabel='Try again' onAction={props.reloadActionSizes} />}

                {!sizesError && sizeData == null &&
                    <div className='mv-size-groups'>
                        <Skeleton height={140} radius='14px' />
                        <Skeleton height={140} radius='14px' />
                    </div>}

                {sizeData != null &&
                    <div className='mv-size-groups'>
                        {renderSizeGroup('Months', 'MONTH', sizeData.months)}
                        {renderSizeGroup('Years', 'YEAR', sizeData.years)}
                    </div>}
            </div>

            <div id='inv-panel-brands' role='tabpanel' aria-labelledby='inv-tab-brands' hidden={tab !== 'brands'}>
                {brandsError && brands == null &&
                    <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load brands'
                        text='Please check your connection and try again.' actionLabel='Try again' onAction={props.reloadActionBrands} />}

                {!brandsError && brands == null &&
                    <section className='mv-card mv-inv-brands' aria-hidden='true'>
                        <ul className='mv-inv-brand-list'>
                            {Array.from({ length: 6 }, (_, i) =>
                                <li key={i} className='mv-inv-brand'><Skeleton height={36} style={{ margin: '6px 0' }} /></li>)}
                        </ul>
                    </section>}

                {brands != null && brands.length === 0 &&
                    <EmptyState icon={<HiOutlineTag />} title='No brands yet'
                        text='Add the brands you sell, then choose one on each product. A brand is optional.'
                        actionLabel='New brand' onAction={() => openBrand(null)} />}

                {brands != null && brands.length > 0 && shownBrands.length === 0 &&
                    <EmptyState icon={<HiOutlineSearch />} title='No brand matches'
                        text='Try another name.' actionLabel='Clear search' onAction={() => setBrandSearch('')} />}

                {shownBrands.length > 0 &&
                    <section className='mv-card mv-inv-brands'>
                        <ul className='mv-inv-brand-list'>
                            {shownBrands.map(b =>
                                <BrandRow key={b.id} brand={b} query={brandQuery} onEdit={openBrand}
                                    flash={flashId === 'brand-' + b.id} />)}
                        </ul>
                    </section>}
            </div>

            <SidePanel open={showCategoryAdd} title={categoryTitle} onClose={closeCategoryForm} busy={panelBusy}
                footer={formActions('inv-category-form', categoryId > 0,
                    () => setConfirmDelete({ type: 'category', id: categoryId, name: category }),
                    Number(parentId) > 0 ? 'Add sub-category' : 'Add category')}>
                {renderCategoryForm()}
            </SidePanel>

            {cropSource &&
                <ImageCropper file={cropSource} steps={[CATEGORY_TILE, CATEGORY_WIDE]} title='Crop category image'
                    onDone={onCropped} onCancel={onCropCancel} />}

            <SidePanel open={showSizeAdd} title={sizeId > 0 ? 'Edit size' : 'New size'} onClose={closeSizeForm} busy={panelBusy}
                footer={formActions('inv-size-form', sizeId > 0,
                    () => setConfirmDelete({ type: 'size', id: sizeId, name: size }), 'Add size')}>
                {renderSizeForm()}
            </SidePanel>

            <SidePanel open={brandEdit != null} title={brandEdit && brandEdit.id > 0 ? 'Edit brand' : 'New brand'}
                onClose={closeBrandForm} busy={panelBusy}
                footer={formActions('inv-brand-form', brandEdit != null && brandEdit.id > 0 && brandUses === 0,
                    () => setConfirmDelete({ type: 'brand', id: brandEdit.id, name: brandName.trim() || brandEdit.name }),
                    brandEdit != null && brandEdit.id > 0 ? 'Save changes' : 'Add brand')}>
                {renderBrandForm()}
            </SidePanel>

            {confirmDelete &&
                <Modal
                    title={`Delete ${confirmDelete.type}?`}
                    onClose={() => setConfirmDelete(null)}
                    busy={isLoading}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setConfirmDelete(null)} disabled={isLoading}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger-solid' onClick={onDeleteConfirmed} disabled={isLoading}>
                            {isLoading ? 'Deleting…' : 'Delete'}
                        </button>
                    </>}
                >
                    <p className='mv-modal-text'>
                        <strong>{confirmDelete.name}</strong> will be permanently deleted. This cannot be undone.
                    </p>
                </Modal>
            }

            <Toast message={toast} onDone={() => setToast('')} />
        </div>
    );
};


export default ShopListCard;
